import { useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';

/**
 * Professional Token Refresh Hook
 * 
 * Implements sliding session expiration by automatically refreshing
 * the JWT token before it expires.
 * 
 * Strategy:
 * - Decodes JWT to know exact expiration time
 * - Schedules refresh 5 minutes before expiration
 * - On successful refresh, reschedules for the new token
 * - On failure, lets the token expire naturally (triggers logout)
 * 
 * @param {string} token - Current JWT token
 * @param {function} onTokenRefreshed - Callback when token is refreshed
 * @param {function} onRefreshFailed - Callback when refresh fails
 */
const useTokenRefresh = (token, onTokenRefreshed, onRefreshFailed) => {
  const refreshTimeoutId = useRef(null);
  const isRefreshing = useRef(false);
  const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes before expiration

  const scheduleRefresh = useCallback((currentToken) => {
    // Clear any existing timeout
    if (refreshTimeoutId.current) {
      clearTimeout(refreshTimeoutId.current);
      refreshTimeoutId.current = null;
    }

    if (!currentToken) {
      console.log('🔄 No token present, skipping refresh schedule');
      return;
    }

    try {
      // Decode token to get expiration
      const decoded = jwtDecode(currentToken);
      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const timeUntilRefresh = timeUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;

      console.log('📅 Token expiration analysis:', {
        expiresAt: new Date(expiresAt).toLocaleTimeString(),
        expiresIn: Math.round(timeUntilExpiry / 1000 / 60) + ' minutes',
        refreshIn: Math.round(timeUntilRefresh / 1000 / 60) + ' minutes'
      });

      // If token expires in less than 5 minutes, refresh immediately
      if (timeUntilRefresh <= 0) {
        console.log('⚡ Token expires soon, refreshing immediately');
        performRefresh(currentToken);
        return;
      }

      // Schedule refresh
      refreshTimeoutId.current = setTimeout(() => {
        console.log('⏰ Scheduled token refresh triggered');
        performRefresh(currentToken);
      }, timeUntilRefresh);

      console.log(`✅ Token refresh scheduled for ${new Date(now + timeUntilRefresh).toLocaleTimeString()}`);
    } catch (error) {
      console.error('❌ Error scheduling token refresh:', error);
    }
  }, []);

  const performRefresh = useCallback(async (currentToken) => {
    // Prevent concurrent refresh attempts
    if (isRefreshing.current) {
      console.log('⏳ Refresh already in progress, skipping...');
      return;
    }

    isRefreshing.current = true;
    console.log('🔄 Performing token refresh...');

    try {
      const response = await api.post('/api/v1/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });

      const { access_token } = response.data;
      
      if (access_token) {
        console.log('✅ Token refreshed successfully');
        
        // Update token in localStorage and axios headers
        localStorage.setItem('token', access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Notify parent component
        if (onTokenRefreshed) {
          onTokenRefreshed(access_token);
        }

        // Schedule next refresh for the new token
        scheduleRefresh(access_token);
      } else {
        throw new Error('No access token received from refresh');
      }
    } catch (error) {
      console.error('❌ Token refresh failed:', error.message);
      
      // Notify parent component of failure
      if (onRefreshFailed) {
        onRefreshFailed(error);
      }
      
      // Don't reschedule - let the token expire naturally
      // The api interceptor will handle the 401 and trigger logout
    } finally {
      isRefreshing.current = false;
    }
  }, [onTokenRefreshed, onRefreshFailed, scheduleRefresh]);

  // Setup refresh schedule when token changes
  useEffect(() => {
    if (token) {
      console.log('🔄 Token detected, scheduling automatic refresh');
      scheduleRefresh(token);
    }

    // Cleanup on unmount or token change
    return () => {
      if (refreshTimeoutId.current) {
        console.log('🧹 Cleaning up token refresh timeout');
        clearTimeout(refreshTimeoutId.current);
        refreshTimeoutId.current = null;
      }
    };
  }, [token, scheduleRefresh]);

  return {
    isRefreshing: isRefreshing.current
  };
};

export default useTokenRefresh;

